import { useState } from 'react'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { ChevronDown, ChevronUp, GripVertical, Palette, Trash2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { FieldRenderer } from '@/components/theme-builder/fields/FieldRenderer'

import { OfferModel } from '@/types'
import { BLOCK_STYLE_SCHEMA, CampaignBlockDef } from '@/lib/campaign-blocks/catalog'
import { cn } from '@/lib/utils'

interface BlockCardProps {
  block: OfferModel.CampaignBlock
  def: CampaignBlockDef
  expanded: boolean
  onToggle: () => void
  onRemove: () => void
  onVariantChange: (variant: string) => void
  onPropChange: (key: string, value: unknown) => void
  onStyleChange: (key: string, value: unknown) => void
  onImageRequest: (path: string) => void
}

export const BlockCard = ({
  block,
  def,
  expanded,
  onToggle,
  onRemove,
  onVariantChange,
  onPropChange,
  onStyleChange,
  onImageRequest,
}: BlockCardProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: block.id,
  })
  const [styleModalOpen, setStyleModalOpen] = useState(false)

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : 0,
    position: 'relative' as const,
    opacity: isDragging ? 0.5 : 1,
  }

  const currentVariant = block.variant ?? def.variants[0]?.id ?? 'v1'
  const currentStyles = block.styles ?? {}

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="overflow-hidden rounded-lg border border-border bg-card"
    >
      <div
        className="flex cursor-pointer items-center justify-between gap-2 px-3 py-2.5 hover:bg-subdued"
        onClick={onToggle}
      >
        <div className="flex min-w-0 items-center gap-2">
          <div {...attributes} {...listeners} className="cursor-grab rounded p-1 text-faint hover:bg-fill hover:text-text-2 active:cursor-grabbing">
            <GripVertical className="size-4" />
          </div>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-ink">{def.label}</p>
            <p className="truncate text-sm text-text-2">{def.description}</p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {def.variants.length > 1 && (
            <div
              className="mr-1 flex items-center gap-0.5 rounded-lg bg-fill p-0.5"
              onClick={(e) => e.stopPropagation()}
            >
              {def.variants.map((v) => (
                <button
                  key={v.id}
                  type="button"
                  onClick={() => onVariantChange(v.id)}
                  className={cn(
                    'rounded-md px-2 py-0.5 text-xs font-medium transition-colors',
                    currentVariant === v.id
                      ? 'bg-card text-ink shadow-z1'
                      : 'text-text-2 hover:text-ink',
                  )}
                >
                  {v.label}
                </button>
              ))}
            </div>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => {
              e.stopPropagation()
              setStyleModalOpen(true)
            }}
            className="text-text-2 hover:text-ink"
            title="Style"
            aria-label="Edit block style"
          >
            <Palette />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => {
              e.stopPropagation()
              onRemove()
            }}
            className="text-error hover:bg-error-lighter"
            title="Delete"
            aria-label="Delete block"
          >
            <Trash2 />
          </Button>
          {def.fields.length > 0 &&
            (expanded ? (
              <ChevronUp className="size-4 text-muted-foreground" />
            ) : (
              <ChevronDown className="size-4 text-muted-foreground" />
            ))}
        </div>
      </div>

      {expanded && def.fields.length > 0 && (
        <div className="space-y-4 border-t border-border-subtle p-4">
          {def.fields.map((field) => {
            if (field.dependency) {
              if (block.props[field.dependency.field] !== field.dependency.value) return null
            }
            return (
              <div key={field.key} className="space-y-1.5">
                <Label>{field.label}</Label>
                <FieldRenderer
                  field={field}
                  value={block.props[field.key]}
                  onChange={(val) => onPropChange(field.key, val)}
                  onImageRequest={onImageRequest}
                  path={field.key}
                />
              </div>
            )
          })}
        </div>
      )}

      <Dialog open={styleModalOpen} onOpenChange={setStyleModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{def.label} style</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {BLOCK_STYLE_SCHEMA.map((field) => {
              if (field.dependency) {
                if (currentStyles[field.dependency.field as keyof typeof currentStyles] !== field.dependency.value) {
                  return null
                }
              }
              return (
                <div key={field.key} className="space-y-1.5">
                  <Label>{field.label}</Label>
                  <FieldRenderer
                    field={field}
                    value={currentStyles[field.key as keyof typeof currentStyles]}
                    onChange={(val) => onStyleChange(field.key, val)}
                    onImageRequest={onImageRequest}
                    path={field.key}
                  />
                </div>
              )
            })}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
